import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Input,
  useToast,
  HStack,
  VStack,
  Text,
  Box,
} from '@chakra-ui/react';
import { Pencil } from 'lucide-react';
import { useRenameList } from '@/api';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';

interface RenameListModalProps {
  isOpen: boolean;
  onClose: () => void;
  listName: string;
  urlKey: string;
}

export function RenameListModal({
  isOpen,
  onClose,
  listName,
  urlKey
}: RenameListModalProps) {
  const toast = useToast();
  const [newListName, setNewListName] = useState(listName);
  const { mutateAsync: renameList, isPending: isRenaming } = useRenameList();

  // Reset the input when the modal opens
  useEffect(() => {
    if (isOpen) {
      setNewListName(listName);
      // Track modal open as edit intent
      analytics.capture(AnalyticsEvents.LIST_NAME_EDIT_CLICKED, {
        list_url_key: urlKey,
      });
    }
  }, [isOpen, listName, urlKey]);

  const handleRename = async () => {
    if (!newListName.trim() || newListName === listName) {
      onClose();
      return;
    }

    try {
      await renameList({
        urlKey,
        listName: newListName
      });

      toast({
        title: "List renamed",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error('Failed to rename list:', error);
      toast({
        title: "Failed to rename list",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="purple.100" borderRadius="var(--r-inner)" p={2}>
              <Pencil size={20} color="var(--chakra-colors-purple-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Rename List
              </Text>
              <Text fontSize="xs" color="var(--card-text-3)" fontWeight="normal">
                Give your list a new name
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <Input
            value={newListName}
            onChange={(e) => setNewListName(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="List name"
            autoFocus
          />
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="var(--card-divider)" pt={4}>
          <HStack spacing={3} w="100%">
            <Button variant="ghost" flex={1} onClick={onClose}>
              Cancel
            </Button>
            <Button
              bg="purple.500"
              color="white"
              _hover={{ bg: 'purple.600' }}
              onClick={handleRename}
              isLoading={isRenaming}
              isDisabled={!newListName.trim() || newListName === listName}
              flex={1}
            >
              Save
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
